/**
 * useApp — the application context: the API adapter, the session, the global period,
 * appearance (theme / text size / exact numbers), toasts, overlays, the refresh flow and the
 * SSE connection that invalidates the query cache.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Appearance, PeriodParam, SseEvent, Theme, User } from '../../../shared-types.ts';
import { createHttpApi, createMockApi, errorMessage, shouldUseMock, type Api } from '../api-select.ts';
import { cacheClear, invalidate, type CacheScope } from './cache.ts';
import { Dialog, Menu, Sheet, Toasts, type DialogSpec, type LayerSpec, type MenuSpec, type SheetSpec, type ToastSpec } from '../components/overlays.tsx';
import { he } from '../i18n/he.ts';

const THEME_KEY = 'pf.theme';
const TEXT_KEY = 'pf.textSize';
const EXACT_KEY = 'pf.exactNumbers';
const PERIOD_KEY = 'pf.period';
const BANNER_KEY = 'pf.bannerHidden';

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode — preferences simply do not persist */
  }
}

export interface AppValue {
  api: Api;
  user: User | null;
  setUser: (user: User | null) => void;
  appearance: Appearance;
  setAppearance: (patch: Partial<Appearance>) => void;
  period: PeriodParam;
  setPeriod: (period: PeriodParam) => void;
  toast: (message: string, options?: { error?: boolean; action?: { label: string; onClick: () => void } }) => void;
  openMenu: (anchor: HTMLElement, content: ReactNode, options?: { wide?: boolean; role?: 'menu' | 'dialog' }) => void;
  openSheet: (spec: Omit<SheetSpec, 'kind'>) => void;
  openDialog: (spec: Omit<DialogSpec, 'kind'>) => void;
  closeLayer: () => void;
  /** True while a manual sync/refresh is running — screens dim to 50% with aria-busy. */
  syncing: boolean;
  startSync: (providerRefresh: boolean) => void;
  /** The `asOf` day the EXPIRED banner was hidden for, or null (spec §3.4 "הסתר להיום"). */
  bannerHiddenFor: string | null;
  hideBannerToday: (day: string) => void;
}

const AppContext = createContext<AppValue | null>(null);

export function useApp(): AppValue {
  const value = useContext(AppContext);
  if (!value) throw new Error('AppProvider missing');
  return value;
}

export function AppProvider({ children, onUnauthenticated }: { children: ReactNode; onUnauthenticated: () => void }) {
  const apiRef = useRef<Api | null>(null);
  if (!apiRef.current) {
    apiRef.current = shouldUseMock() ? createMockApi() : createHttpApi({ onUnauthenticated });
  }
  const api = apiRef.current;

  const [user, setUser] = useState<User | null>(null);
  const [appearance, setAppearanceState] = useState<Appearance>(() => ({
    theme: (readLocal(THEME_KEY) as Theme | null) ?? 'auto',
    textSize: readLocal(TEXT_KEY) === 'large' ? 'large' : 'normal',
    exactNumbers: readLocal(EXACT_KEY) === '1',
  }));
  const [period, setPeriodState] = useState<PeriodParam>(() => (readLocal(PERIOD_KEY) as PeriodParam | null) ?? 'month');
  const [toasts, setToasts] = useState<ToastSpec[]>([]);
  const [layer, setLayer] = useState<LayerSpec | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [bannerHiddenFor, setBannerHiddenFor] = useState<string | null>(() => readLocal(BANNER_KEY));
  const lastFocus = useRef<HTMLElement | null>(null);
  const toastSeq = useRef(0);

  // theme + text size on <html> (tokens.css reads `data-theme`)
  useEffect(() => {
    const root = document.documentElement;
    if (appearance.theme === 'auto') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', appearance.theme);
  }, [appearance.theme]);

  const setAppearance = useCallback(
    (patch: Partial<Appearance>) => {
      setAppearanceState((prev) => {
        const next = { ...prev, ...patch };
        if (patch.theme) writeLocal(THEME_KEY, patch.theme);
        if (patch.textSize) writeLocal(TEXT_KEY, patch.textSize);
        if (patch.exactNumbers !== undefined) writeLocal(EXACT_KEY, patch.exactNumbers ? '1' : '0');
        return next;
      });
    },
    [],
  );

  const setPeriod = useCallback((next: PeriodParam) => {
    writeLocal(PERIOD_KEY, next);
    setPeriodState(next);
  }, []);

  const toast = useCallback((message: string, options?: { error?: boolean; action?: { label: string; onClick: () => void } }) => {
    toastSeq.current += 1;
    const id = toastSeq.current;
    setToasts((prev) => [...prev, { id, message, error: options?.error, action: options?.action }].slice(-2));
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), options?.action ? 5000 : 4000);
  }, []);

  const closeLayer = useCallback(() => {
    setLayer(null);
    const focus = lastFocus.current;
    lastFocus.current = null;
    if (focus?.isConnected) focus.focus({ preventScroll: true });
  }, []);

  const openMenu = useCallback((anchor: HTMLElement, content: ReactNode, options?: { wide?: boolean; role?: 'menu' | 'dialog' }) => {
    lastFocus.current = anchor;
    setLayer({ kind: 'menu', anchor: anchor.getBoundingClientRect(), content, wide: options?.wide, role: options?.role });
  }, []);

  const openSheet = useCallback((spec: Omit<SheetSpec, 'kind'>) => {
    lastFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setLayer({ kind: 'sheet', ...spec });
  }, []);

  const openDialog = useCallback((spec: Omit<DialogSpec, 'kind'>) => {
    lastFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setLayer({ kind: 'dialog', ...spec });
  }, []);

  const hideBannerToday = useCallback((day: string) => {
    writeLocal(BANNER_KEY, day);
    setBannerHiddenFor(day);
  }, []);

  // SSE — invalidate the query cache and surface sync toasts (api-personal.md §3.11)
  useEffect(() => {
    if (!user) return undefined;
    return api.subscribe((event: SseEvent) => {
      switch (event.event) {
        case 'sync:start':
          setSyncing(true);
          break;
        case 'sync:done':
          setSyncing(false);
          invalidate(['summary', 'transactions', 'categories', 'cards', 'networth', 'accounts', 'connections', 'alerts']);
          toast(he.refresh.ok);
          break;
        case 'sync:error':
          setSyncing(false);
          toast(event.data.message || he.refresh.failed, { error: true });
          break;
        case 'alerts:changed':
          invalidate(['alerts', 'summary']);
          break;
        case 'data:changed':
          invalidate(event.data.scopes as CacheScope[]);
          break;
        default:
          break;
      }
    });
  }, [api, user, toast]);

  const startSync = useCallback(
    (providerRefresh: boolean) => {
      setSyncing(true);
      api.sync(providerRefresh).then(
        () => {
          /* progress and completion arrive over SSE */
        },
        (error: unknown) => {
          setSyncing(false);
          toast(errorMessage(error), { error: true });
        },
      );
    },
    [api, toast],
  );

  const value = useMemo<AppValue>(
    () => ({
      api,
      user,
      setUser: (next) => {
        if (!next) cacheClear();
        setUser(next);
      },
      appearance,
      setAppearance,
      period,
      setPeriod,
      toast,
      openMenu,
      openSheet,
      openDialog,
      closeLayer,
      syncing,
      startSync,
      bannerHiddenFor,
      hideBannerToday,
    }),
    [api, user, appearance, setAppearance, period, setPeriod, toast, openMenu, openSheet, openDialog, closeLayer, syncing, startSync, bannerHiddenFor, hideBannerToday],
  );

  return (
    <AppContext.Provider value={value}>
      <div className={appearance.textSize === 'large' ? 'app text-lg' : 'app'}>
        {children}
        {layer?.kind === 'menu' ? <Menu spec={layer} onClose={closeLayer} /> : null}
        {layer?.kind === 'sheet' ? <Sheet spec={layer} onClose={closeLayer} /> : null}
        {layer?.kind === 'dialog' ? <Dialog spec={layer} onClose={closeLayer} /> : null}
        <Toasts items={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))} />
      </div>
    </AppContext.Provider>
  );
}
